import { CREATED_BY } from "@sajilni/config";
import { calculateItemTotalsWithTax } from "@sajilni/money/helpers";
import { createOrderTransitionV2 } from "services/Transitions";
import {
  getEffectiveBookableItem,
  type EntityWithRelations,
} from "services/bookableItem/helpers";
import { getTicketAvailability } from "services/ticketQuantity";
import { checkItemQuantityValidity } from "services/order";
import { getIsDelayedPaymentGateway } from "services/paymentGateway";
import { createPayment } from "services/payment";
import { createInvoice } from "services/invoice";
import { validateOrderRestrictions } from "../restriction/validation";
import {
  claimSeat,
  confirmSeatsByOrderItemIds,
  releaseSeats,
} from "../seatingBuilder/seat-override";
import {
  isSeatWithinConfig,
  shouldActivateSeatSection,
} from "../seatingBuilder/seat-section-rules";
import { processPricingRulesForOrder } from "../pricing-rules";
import { warmCheckinRosterEntriesForOrder } from "../checkInOut/warmCheckinCache";
import { syncInvitationAttendeesAfterOrderComplete } from "../invitations";
import type { PricingCalculationResult } from "../pricing-rules";
import {
  prisma,
  OrderStatus,
  OrderType,
  EntityStatus,
  EntityType,
  OrderItemStatus,
  PaymentStatus,
  FormResultStatus,
  FormResultType,
  PaymentMethod,
  InvoiceStatus,
  SeatOverrideStatus,
  SeatSectionStatus,
  type Prisma,
  type LineItem,
  type Currency,
  type OrderItem,
  type Payment,
  type Exhibitor,
  type GatewayMethod,
  type Order,
} from "client";
import type { CreateOrderV2Payload, UpdateOrderV2Payload } from "./types";

const calculateExpiryDate = (expiryDuration: number) => {
  const expiryDate = new Date();
  expiryDate.setMinutes(expiryDate.getMinutes() + expiryDuration);
  return expiryDate;
};

export const generateOrderNumberV2 = async (
  eventId: string,
  tx?: Prisma.TransactionClient
) => {
  try {
    const db = tx ?? prisma;
    const latestOrder = await db.order.findFirst({
      where: { eventId },
      orderBy: { number: "desc" },
      select: { number: true },
    });
    return (latestOrder?.number || 0) + 1;
  } catch (error) {
    throw new Error(`Failed to generate order number: ${error}`);
  }
};

interface CalculateItemSubTotalAndTaxParams {
  itemTotal: number;
  subUnit: number;
  currency: Currency;
  isTaxable: boolean;
  isEventTaxConfigured: boolean;
  taxRate: number;
  itemType: "ticket" | "entity";
}

const validateItemTaxConfiguration = (
  isTaxable: boolean,
  isEventTaxConfigured: boolean,
  itemTotal: number,
  itemType: "ticket" | "entity"
) => {
  // Validate if tax is configured for paid taxable item
  if (isTaxable && itemTotal > 0 && !isEventTaxConfigured) {
    throw new Error(
      `This ${itemType} is taxable. Please configure tax for this event`
    );
  }
};

/**
 * Attach freshly created seat order items to the overrides that hold them.
 * The claim has to happen before the order item exists (it is the availability
 * check), so the back-reference is filled in right after the order is written.
 */
const linkSeatOverridesToOrderItems = async (
  orderItems: Pick<OrderItem, "id" | "seatSectionId" | "row" | "seat">[],
  tx?: Prisma.TransactionClient
) => {
  const db = tx ?? prisma;
  const seatItems = orderItems.filter(
    (item) =>
      item.seatSectionId != null && item.row != null && item.seat != null
  );
  if (seatItems.length === 0) return;

  await Promise.all(
    seatItems.map((item) =>
      db.seatOverride.updateMany({
        where: {
          seatSectionId: item.seatSectionId!,
          row: item.row!,
          seat: item.seat!,
        },
        data: { orderItemId: item.id },
      })
    )
  );
};

export const checkEntityAvailabilityV2 = (status: EntityStatus) => {
  const isAvailable = status === EntityStatus.AVAILABLE;

  if (!isAvailable) {
    throw new Error("Entity is not available");
  }
};

interface CreateOrderItemPayload {
  requestedQuantity: number;
  finalQuantity: number;
  currency: Currency;
  taxRate: number;
  isEventTaxConfigured: boolean;
  inviteeId?: string;
  isAdminOrder?: boolean;
  label: string;
  /** Derived seat inside a SEATS_SECTION entity, if this item is a seat. */
  seat?: { row: number; seat: number };
  /** Hold expiry mirrored onto the seat override for the cron sweep. */
  expiryDate?: Date;
}

const createEntityOrderItemHandler = async (
  entityId: string,
  payload: Omit<CreateOrderItemPayload, "finalQuantity">,
  tx?: Prisma.TransactionClient
) => {
  try {
    const db = tx ?? prisma;
    const {
      requestedQuantity,
      currency,
      taxRate,
      isEventTaxConfigured,
      inviteeId,
      isAdminOrder,
      label,
      seat,
      expiryDate,
    } = payload;

    const entity = await db.entity.findUnique({
      where: { id: entityId },
      select: {
        status: true,
        label: true,
        type: true,
        seatSection: true,
        configSource: true,
        entityTierId: true,
        properties: {
          select: {
            enableBookingRequest: true,
          },
        },
        entityTier: {
          select: {
            name: true,
            bookableItem: {
              select: {
                price: true,
                taxable: true,
                quantity: true,
                minAmount: true,
                maxAmount: true,
                soldTogether: true,
                requireApproval: true,
              },
            },
          },
        },
        bookableItem: {
          select: {
            price: true,
            taxable: true,
            quantity: true,
            minAmount: true,
            maxAmount: true,
            soldTogether: true,
            requireApproval: true,
          },
        },
      },
    });
    if (!entity) {
      throw new Error("Entity not found");
    }

    const isSeatsSection = entity.type === EntityType.SEATS_SECTION;
    if (isSeatsSection !== !!seat) {
      throw new Error(
        isSeatsSection
          ? "A seats section can only be booked one seat at a time"
          : "Seat coordinates are only valid for a seats section"
      );
    }

    // A section is a container, never "taken" — availability is per seat.
    if (!isSeatsSection) {
      checkEntityAvailabilityV2(entity.status);
    } else if (!isSeatWithinConfig(entity.seatSection, seat!.row, seat!.seat)) {
      throw new Error("Seat does not exist in this section");
    }

    const effectiveBookableItem = getEffectiveBookableItem(
      entity as EntityWithRelations
    );
    const { price, taxable, requireApproval, ...quantityValidation } =
      effectiveBookableItem;
    // TODO: later we should decide what option to use here Enable booking request or require approval we should just read requireApproval from bookableItem after implementing inheritance for bookableItem!
    const isRequest = entity.properties.enableBookingRequest || requireApproval;

    // requestedQuantity for entity is always 1
    if (requestedQuantity !== 1) {
      throw new Error(
        "Requested quantity for entity must be 1, for now we only support one entity per order item"
      );
    }

    // check if entity quantity is valid
    // TODO: the below function should validate entities per order not order item
    // checkItemQuantityValidity(requestedQuantity, quantityValidation, "entity");

    // Calculate totals
    // TODO: later we should detect if the order is not based on base price but on price variant
    const entityPrice = price || 0;
    const isTaxable = taxable || false;

    validateItemTaxConfiguration(
      isTaxable,
      isEventTaxConfigured,
      entityPrice,
      "entity"
    );

    const { singleItemTotalMinor, singleItemTaxMinor } =
      calculateItemTotalsWithTax({
        amountMinor: entityPrice,
        quantity: requestedQuantity,
        currencyCode: currency,
        taxRate,
        isTaxable,
      });

    let orderItemStatus: OrderItemStatus = OrderItemStatus.RESERVED;

    if (isRequest) {
      orderItemStatus = OrderItemStatus.PENDING_APPROVAL;
    }

    if (isAdminOrder) {
      orderItemStatus = OrderItemStatus.CONFIRMED;
    }

    // Create order item
    const orderItem: Prisma.OrderItemCreateManyOrderInput = {
      total: singleItemTotalMinor,
      tax: singleItemTaxMinor,
      taxRate,
      taxable: isTaxable,
      entityId,
      status: orderItemStatus,
      ...(seat
        ? {
            seatSectionId: entityId,
            row: seat.row,
            seat: seat.seat,
            // Snapshot: the section may be renumbered after this sale.
            seatLabel: label,
          }
        : {}),
      ...(inviteeId ? { invitationAttendeeId: inviteeId } : {}),
    };

    if (seat) {
      // The unique index on (seatSectionId,row,seat) IS the availability check:
      // create first, and a P2002 means another buyer got here first.
      const claim = await claimSeat(
        {
          seatSectionId: entityId,
          row: seat.row,
          seat: seat.seat,
          status: isAdminOrder
            ? SeatOverrideStatus.RESERVED
            : SeatOverrideStatus.ON_HOLD,
          label,
          price: entityPrice,
          expiresAt: isAdminOrder ? null : expiryDate ?? null,
        },
        db
      );
      if (!claim.ok) {
        throw new Error(`Seat ${label} is no longer available`);
      }

      // First claim freezes the layout: seat identity can no longer be edited.
      const seatSectionConfig = entity.seatSection;
      if (
        seatSectionConfig &&
        shouldActivateSeatSection({
          config: seatSectionConfig,
          hasNonDraftOverride: true,
          isTierSaleOpen: false,
        })
      ) {
        await db.entity.update({
          where: { id: entityId },
          data: {
            seatSection: {
              set: {
                ...seatSectionConfig,
                status: SeatSectionStatus.ACTIVE,
              },
            },
          },
        });
      }
    } else {
      const entityStatus: EntityStatus = isAdminOrder
        ? EntityStatus.RESERVED
        : EntityStatus.ON_HOLD;
      // Reserve the entity — never done for a section, which holds many seats
      await db.entity.update({
        where: { id: entityId },
        data: { status: entityStatus },
      });
    }

    // Create line item — one per seat, so the id has to carry the seat identity
    const lineItem: LineItem = {
      id: seat ? `${entityId}:${seat.row}:${seat.seat}` : entityId,
      name: label,
      description: label,
      quantity: requestedQuantity,
      price: entityPrice,
      image: "",
      taxable: isTaxable,
    };

    return {
      itemSubTotal: singleItemTotalMinor,
      itemTax: singleItemTaxMinor,
      orderItem,
      lineItem,
      entityTier: {
        id: entity.entityTierId,
        name: entity.entityTier?.name || "",
        quantity: {
          min: quantityValidation.minAmount,
          max: quantityValidation.maxAmount,
          soldTogether: quantityValidation.soldTogether,
        },
      },
    };
  } catch (error) {
    throw new Error(`Failed to create entity order item: ${error}`);
  }
};

const createTicketOrderItemsHandler = async (
  ticketId: string,
  payload: CreateOrderItemPayload,
  tx?: Prisma.TransactionClient
) => {
  try {
    const db = tx ?? prisma;
    const {
      requestedQuantity,
      finalQuantity,
      currency,
      taxRate,
      isEventTaxConfigured,
      inviteeId,
      isAdminOrder,
      label,
    } = payload;
    const lineItems: LineItem[] = [];
    const orderItems: Prisma.OrderItemCreateManyOrderInput[] = [];

    const ticket = await db.ticket.findUnique({
      where: { id: ticketId },
      select: {
        bookableItem: {
          select: {
            price: true,
            taxable: true,
            quantity: true,
            minAmount: true,
            maxAmount: true,
            soldTogether: true,
            requireApproval: true,
          },
        },
        category: {
          select: {
            isSharedQuantity: true,
            tickets: {
              select: {
                id: true,
              },
            },
          },
        },
      },
    });
    if (!ticket) {
      throw new Error(`Ticket not found`);
    }

    const { price, taxable, requireApproval, ...quantityValidation } =
      ticket.bookableItem;

    // check if ticket quantity is valid
    // Admin orders don't need to check quantity validity
    if (!isAdminOrder) {
      checkItemQuantityValidity(finalQuantity, quantityValidation, "ticket");
    }

    const { availableTickets } = await getTicketAvailability(ticketId, db);
    const hasEnoughQuantity = availableTickets >= requestedQuantity;

    // requireApproval tickets bypass inventory — organizer enforces limit at approval time
    if (!requireApproval && !hasEnoughQuantity) {
      throw new Error("Not enough tickets available");
    }

    // Calculate totals for order items (using requestedQuantity - the delta)
    const ticketPrice = price || 0;
    const isTaxable = taxable || false;

    validateItemTaxConfiguration(
      isTaxable,
      isEventTaxConfigured,
      ticketPrice,
      "ticket"
    );

    const {
      singleItemTotalMinor,
      singleItemTaxMinor,
      itemsSubTotalMinor,
      itemsTaxMinor,
    } = calculateItemTotalsWithTax({
      amountMinor: ticketPrice,
      quantity: requestedQuantity,
      currencyCode: currency,
      taxRate,
      isTaxable,
    });

    let orderItemStatus: OrderItemStatus = OrderItemStatus.RESERVED;

    if (requireApproval) {
      orderItemStatus = OrderItemStatus.PENDING_APPROVAL;
    }

    if (isAdminOrder) {
      orderItemStatus = OrderItemStatus.CONFIRMED;
    }

    // Create individual order items for each ticket quantity (requestedQuantity is the delta to add)
    for (let i = 0; i < requestedQuantity; i++) {
      orderItems.push({
        total: singleItemTotalMinor,
        tax: singleItemTaxMinor,
        taxRate,
        taxable: isTaxable,
        ticketId,
        status: orderItemStatus,
        ...(inviteeId ? { invitationAttendeeId: inviteeId } : {}),
      });
    }

    // Line item uses finalQuantity (total target quantity) and total price
    const finalItemTotal = finalQuantity * ticketPrice;
    lineItems.push({
      id: ticketId,
      name: label,
      description: label,
      quantity: finalQuantity, // Use finalQuantity for line item
      price: finalItemTotal, // Total price for finalQuantity
      image: "",
      taxable: isTaxable,
    });

    return {
      itemsSubTotal: itemsSubTotalMinor,
      itemsTax: itemsTaxMinor,
      orderItems,
      lineItems,
    };
  } catch (error) {
    throw new Error(`Failed to create ticket order item: ${error}`);
  }
};

interface CreateOrderV2Type extends Omit<CreateOrderV2Payload, "isDTCM"> {
  createdBy: {
    id?: string;
    name?: string;
  };
  profileId?: string;
}

const checkAmounts = (
  itemResults: {
    entityTier: {
      id: string;
      quantity: { max: number; min: number; soldTogether: number };
      name: string;
    };
  }[]
) => {
  const tiersSeats = new Map<string, number>();

  const tiers = itemResults.map((result) => {
    if (result.entityTier) {
      tiersSeats.set(
        result.entityTier.id,
        (tiersSeats.get(result.entityTier.id) || 0) + 1
      );
    }
    return result.entityTier;
  });
  const errors: string[] = [];
  tiersSeats.forEach((seats, tierId) => {
    const tier = tiers.find((t) => t?.id === tierId);
    if (tier?.quantity?.max && seats > tier.quantity.max) {
      errors.push(
        `Tier ${tier.name} has reached the max amount of seats: ${tier.quantity.max}`
      );
    }
    if (tier?.quantity?.min && seats < tier.quantity.min) {
      errors.push(
        `Tier ${tier.name} has not reached the min amount of seats: ${tier.quantity.min}`
      );
    }
    if (
      tier?.quantity?.soldTogether &&
      seats % tier.quantity.soldTogether !== 0
    ) {
      errors.push(
        `Tier ${tier.name}  sold together amount of seats: ${tier.quantity.soldTogether}`
      );
    }
  });
  if (errors.length > 0) {
    throw new Error(errors.join("\n"));
  }
};

export const createOrderV2 = async ({
  selectedItems,
  eventId,
  type,
  lang,
  createdBy = {
    id: CREATED_BY.GUEST,
    name: CREATED_BY.GUEST,
  },
  isAdminOrder,
  selectedPaymentMethodId,
  adminQuickFormData,
  profileId,
}: CreateOrderV2Type) => {
  try {
    // Get event with necessary details
    const event = await prisma.event.findUnique({
      where: { id: eventId },
      select: {
        currency: true,
        orderExpiryDurationInMinutes: true,
        eventMethodConfigs: {
          where: { disabled: false },
          select: {
            gatewayMethod: {
              select: {
                gatewayConfiguration: {
                  select: {
                    paymentGateway: { select: { disabled: true } },
                  },
                },
              },
            },
          },
        },
        restrictions: {
          where: {
            enabled: true,
          },
        },
        taxRegion: {
          select: { saleTaxRate: true },
        },
      },
    });

    if (!event) {
      throw new Error("Event not found");
    }

    // Validate restrictions if order type is TICKET or ENTITY
    // Validation works even without profileId (validates current selection only)
    // Profile will be connected when order is completed
    if (
      (type === OrderType.TICKET || type === OrderType.ENTITY) &&
      event?.restrictions?.length > 0
    ) {
      const restrictionValidation = await validateOrderRestrictions(
        event?.restrictions,
        profileId,
        type === OrderType.TICKET ? "TICKET" : "ENTITY",
        selectedItems
      );
      if (!restrictionValidation.valid) {
        throw new Error(restrictionValidation.error);
      }
    }
    return await prisma.$transaction(
      async (tx) => {
        const createdByName = createdBy.name || CREATED_BY.GUEST;
        const createdById = createdBy.id || CREATED_BY.GUEST;

        // Calculate pricing and totals
        const currency = event.currency;
        const taxRate = event.taxRegion?.saleTaxRate || 0;
        const isEventTaxConfigured = Boolean(event.taxRegion);

        const orderItems: Prisma.OrderItemCreateManyOrderInput[] = [];
        const lineItems: LineItem[] = [];

        // Needed before item creation: seat holds carry the same expiry so the
        // sweep can reclaim them without joining back to the order.
        const expiryDate = calculateExpiryDate(
          event.orderExpiryDurationInMinutes || 60
        );

        // Process all items in parallel since they don't depend on each other
        const itemResults = await Promise.all(
          selectedItems.map(async (item) => {
            const {
              entityId,
              ticketId,
              quantity: requestedQuantity,
              inviteeId,
              label,
              row,
              seat,
            } = item;

            if (type === OrderType.ENTITY) {
              if (!entityId) {
                throw new Error("Entity Id is required for ENTITY order type");
              }

              const { itemSubTotal, itemTax, orderItem, lineItem, entityTier } =
                await createEntityOrderItemHandler(
                  entityId,
                  {
                    requestedQuantity,
                    currency,
                    taxRate,
                    isEventTaxConfigured,
                    inviteeId,
                    isAdminOrder,
                    label,
                    ...(row != null && seat != null
                      ? { seat: { row, seat }, expiryDate }
                      : {}),
                  },
                  tx
                );

              return {
                itemsSubTotal: itemSubTotal,
                itemsTax: itemTax,
                orderItems: [orderItem],
                lineItems: [lineItem],
                entityTier,
              };
            } else {
              if (!ticketId) {
                throw new Error("Ticket Id is required for TICKET order type");
              }

              const {
                itemsSubTotal,
                itemsTax,
                orderItems: ticketOrderItems,
                lineItems: ticketLineItems,
              } = await createTicketOrderItemsHandler(
                ticketId,
                {
                  requestedQuantity,
                  // final quantity is the same as requested quantity in create order
                  finalQuantity: requestedQuantity,
                  currency,
                  taxRate,
                  isEventTaxConfigured,
                  inviteeId,
                  isAdminOrder,
                  label,
                },
                tx
              );

              return {
                itemsSubTotal,
                itemsTax,
                orderItems: ticketOrderItems,
                lineItems: ticketLineItems,
              };
            }
          })
        );
        checkAmounts(
          itemResults as {
            entityTier: {
              id: string;
              quantity: { max: number; min: number; soldTogether: number };
              name: string;
            };
          }[]
        );
        let subTotal = 0;
        let tax = 0;
        let total = 0;

        // Accumulate all results
        for (const result of itemResults) {
          subTotal += result.itemsSubTotal;
          tax += result.itemsTax;
          orderItems.push(...result.orderItems);
          lineItems.push(...result.lineItems);
        }

        total = subTotal + tax;

        const isPaidOrder = total > 0;
        const paymentGateways = event.eventMethodConfigs
          .map(
            (config) =>
              config.gatewayMethod.gatewayConfiguration?.paymentGateway
          )
          .filter(
            (gateway): gateway is NonNullable<typeof gateway> => !!gateway
          );
        const hasPaymentGatewayConfigured = paymentGateways.length > 0;
        const hasPaymentGatewayEnabled = paymentGateways.some(
          (gateway) => gateway.disabled === false
        );

        // Validate payment gateway and tax for paid orders
        if (isPaidOrder) {
          if (!hasPaymentGatewayConfigured) {
            throw new Error("Please configure payment gateway for paid orders");
          }
          if (!hasPaymentGatewayEnabled) {
            throw new Error("Please enable payment gateway for paid orders");
          }
          if (isAdminOrder && !selectedPaymentMethodId) {
            throw new Error(
              "Please select a payment method for paid admin order"
            );
          }
        }

        // Generate order number - find the latest order and increment
        const orderNumber = await generateOrderNumberV2(eventId, tx);

        // ===== ADMIN ORDER PAYMENT LOGIC =====
        // For admin orders, we need to:
        // 1. Validate the selected payment method exists
        // 2. Check if it's a FREE payment type (complimentary)
        // 3. Adjust totals accordingly (FREE payments = 0 amounts)
        // 4. Set order status to COMPLETED (admin orders are immediately completed)
        let method: Pick<GatewayMethod, "type"> | null = null;
        const shouldCreateAdminPayment =
          isAdminOrder && selectedPaymentMethodId && isPaidOrder;

        if (shouldCreateAdminPayment) {
          method = await tx.gatewayMethod.findUnique({
            where: { id: selectedPaymentMethodId },
            select: { type: true },
          });

          if (!method) {
            throw new Error("Selected payment method not found");
          }
        }

        // For FREE payment method, set all amounts to 0 (complimentary order)
        const isFreePayment = method?.type === PaymentMethod.FREE;
        const adminTotal = isFreePayment ? 0 : total;
        const adminSubTotal = isFreePayment ? 0 : subTotal;
        const adminTax = isFreePayment ? 0 : tax;

        // Set order status and totals based on order type
        const orderStatus: OrderStatus = isAdminOrder
          ? OrderStatus.COMPLETED
          : OrderStatus.ON_GOING;
        const orderTotal = isAdminOrder ? adminTotal : total;
        const orderSubTotal = isAdminOrder ? adminSubTotal : subTotal;
        const orderTax = isAdminOrder ? adminTax : tax;

        // Create order with order items
        const order = await tx.order.create({
          data: {
            number: orderNumber,
            status: orderStatus,
            orderItems: { createMany: { data: orderItems } },
            lineItems,
            subTotal: orderSubTotal,
            tax: orderTax,
            total: orderTotal,
            taxRate,
            currency,
            type,
            lang,
            expiryDate,
            event: { connect: { id: eventId } },
            ...(isAdminOrder && {
              createdByAdmin: { connect: { id: createdById } },
              ...(adminQuickFormData && {
                formResults: {
                  create: {
                    type: FormResultType.ORDER,
                    formResult: {
                      create: {
                        surveyResult: adminQuickFormData,
                        surveyResultText: JSON.stringify(adminQuickFormData),
                      },
                    },
                  },
                },
              }),
            }),
          },
          include: {
            payments: true,
            formResults: {
              take: 1,
              select: {
                formResult: true,
              },
            },
            orderItems: {
              include: {
                formResults: {
                  select: {
                    type: true,
                    formResult: {
                      select: {
                        id: true,
                        status: true,
                        surveyResult: true,
                      },
                    },
                  },
                },
                ticket: {
                  select: {
                    id: true,
                    bookableItem: {
                      select: {
                        id: true,
                        translations: {
                          where: {
                            language: {
                              code: lang,
                            },
                          },
                          select: {
                            name: true,
                            description: true,
                          },
                        },
                        formEntities: {
                          select: {
                            type: true,
                            formTemplate: {
                              select: {
                                id: true,
                                json: true,
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
                entity: {
                  select: {
                    id: true,
                    label: true,
                    type: true,
                    properties: {
                      select: {
                        enableBookingRequest: true,
                      },
                    },
                    floorPlan: {
                      select: {
                        settings: {
                          select: {
                            enableBookingRequest: true,
                          },
                        },
                        formEntities: {
                          select: {
                            type: true,
                            formTemplate: {
                              select: {
                                id: true,
                                json: true,
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
                invitationAttendee: {
                  select: {
                    id: true,
                    invitation: {
                      select: {
                        id: true,
                        translations: {
                          where: {
                            language: {
                              code: lang,
                            },
                          },
                          select: {
                            name: true,
                            description: true,
                          },
                        },
                        formEntity: {
                          select: {
                            formTemplate: {
                              select: {
                                id: true,
                                json: true,
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });

        // Seat overrides were claimed before the order items existed; link them
        // now so release / confirm can find them by orderItemId.
        await linkSeatOverridesToOrderItems(order.orderItems, tx);

        // Create order transition
        await createOrderTransitionV2(
          {
            status: order.status,
            orderId: order.id,
            createdBy: createdByName,
            metaData: {
              createdBy: createdByName,
              createdById,
            },
          },
          tx
        );

        let payment: Payment | null = null;

        if (shouldCreateAdminPayment) {
          // Create payment for admin order
          payment = await createPayment(
            {
              status: PaymentStatus.PAID,
              grandTotal: adminTotal,
              subTotal: adminSubTotal,
              totalTax: adminTax,
              paymentFees: 0,
              totalCharge: 0,
              totalDiscountAmount: 0,
              currency,
              order: { connect: { id: order.id } },
              event: { connect: { id: eventId } },
              paymentMethod: { connect: { id: selectedPaymentMethodId } },
            },
            eventId,
            {
              createdBy: createdByName,
              metaData: {
                createdBy: createdByName,
                createdById,
                paymentMethodType: method?.type,
                ...(isFreePayment && {
                  note: "Complimentary order - no payment required",
                  originalOrderTotal: total,
                }),
              },
              selectedPaymentMethodId,
            },
            tx
          );

          // Create invoice for the payment
          await createInvoice(
            {
              status: InvoiceStatus.PAID,
              order: { connect: { id: order.id } },
              payment: { connect: { id: payment.id } },
              total: adminTotal,
              subTotal: adminSubTotal,
              tax: adminTax,
              currency,
              lineItems,
            },
            eventId,
            tx
          );
        }

        // TODO: later
        // Create order items transitions

        const firstFormResult =
          "formResults" in order
            ? (
                order as {
                  formResults?: Array<{
                    formResult?: unknown;
                  }>;
                }
              ).formResults?.[0]?.formResult
            : undefined;
        const payments = payment && isAdminOrder ? [payment] : [];

        return { ...order, formResult: firstFormResult, payments };
      },
      {
        timeout: 10000,
        maxWait: 10000,
      }
    );
  } catch (error) {
    console.error("Error creating order:", error);
    throw error;
  }
};

type ItemToCreate = CreateOrderV2Payload["selectedItems"][number] & {
  finalQuantity: number;
};

const calculateOrderItemsDiff = (
  orderType: OrderType,
  currentItems: OrderItem[],
  newSelections: CreateOrderV2Payload["selectedItems"]
) => {
  const isEntityOrder = orderType === OrderType.ENTITY;

  const currentMap = new Map<string, string[]>();
  const toDelete: string[] = [];
  const toCreate: ItemToCreate[] = [];

  // Build current state map. Derived seats key on the seat, not the section:
  // two seats of one section are two distinct selections, not quantity 2.
  currentItems.forEach((item) => {
    const { id, ticketId, entityId, seatSectionId, row, seat } = item;

    const itemId =
      isEntityOrder && seatSectionId != null
        ? `${seatSectionId}:${row}:${seat}`
        : isEntityOrder
        ? entityId
        : ticketId;
    if (!itemId) {
      throw new Error(
        `${
          isEntityOrder ? "Entity" : "Ticket"
        } Id is required for ${orderType} order type`
      );
    }
    if (!currentMap.has(itemId)) {
      currentMap.set(itemId, []);
    }
    currentMap.get(itemId)?.push(id);
  });

  // Calculate changes
  for (const selection of newSelections) {
    const { quantity, ticketId, entityId, inviteeId, label, row, seat } =
      selection;
    const isSeat = isEntityOrder && row != null && seat != null;

    const selectionId = isSeat
      ? `${entityId}:${row}:${seat}`
      : isEntityOrder
      ? entityId
      : ticketId;
    if (!selectionId) {
      throw new Error(
        `${
          isEntityOrder ? "Entity" : "Ticket"
        } Id is required for ${orderType} order type`
      );
    }

    // Validate entity quantity is always 1
    if (isEntityOrder && quantity !== 1) {
      throw new Error("Entity quantity must be 1");
    }

    const currentIds = currentMap.get(selectionId) || [];
    const currentQuantity = currentIds.length;
    const diff = quantity - currentQuantity;
    const finalQuantity = quantity;

    if (diff > 0) {
      // Add items with quantity information
      toCreate.push({
        ...(isEntityOrder ? { entityId } : { ticketId }),
        ...(inviteeId ? { inviteeId } : {}),
        ...(isSeat ? { seatSectionId: entityId, row, seat } : {}),
        quantity: diff, // Preserve the quantity difference
        finalQuantity, // Add final quantity for validation
        label,
      });
    } else if (diff < 0) {
      // Remove excess items
      toDelete.push(...currentIds.slice(0, -diff));
    }

    // Mark as processed
    currentMap.delete(selectionId);
  }

  // Add remaining items for deletion (removed tickets/entities)
  for (const remainingIds of currentMap.values()) {
    toDelete.push(...remainingIds);
  }

  return { toDelete, toCreate };
};

const getPaymentsPaidTotal = (payments: Payment[]) =>
  payments
    .filter((p) => p.status === PaymentStatus.PAID)
    .reduce((sum, p) => sum + (p.grandTotal ?? 0), 0);

const ensurePaymentFullySettled = (orderTotal: number, payments: Payment[]) => {
  const paidTotal = getPaymentsPaidTotal(payments ?? []);
  return paidTotal === orderTotal;
};

/**
 * Deletes form results associated with the specified order items
 *
 * This function removes form results that are linked to order items being deleted.
 * It handles the cleanup of form data when order items are removed from an order.
 *
 * @param toDelete - Array of order item IDs whose associated form results should be deleted
 * @param tx - Optional Prisma transaction client for database operations
 * @returns Promise that resolves when the form results have been deleted
 */
const deleteItemsFormResults = async (
  toDelete: string[],
  tx?: Prisma.TransactionClient
) => {
  const db = tx ?? prisma;

  await db.formResult.deleteMany({
    where: {
      formResultEntity: {
        orderItemId: { in: toDelete },
      },
    },
  });
};

/**
 * Releases entities associated with deleted order items by updating their status to AVAILABLE
 * and clearing any exhibitor or invitation attendee associations.
 *
 * This function is used during order updates to properly handle the cleanup of entities
 * that are no longer part of the order, ensuring they become available for other orders.
 *
 * @param deletedOrderItems - Array of deleted order items
 * @param toDelete - Array of order item IDs that are being removed from the order
 * @param tx - Optional Prisma transaction client for database operations
 * @returns Promise that resolves when all entity updates are completed
 */
const releaseEntities = async (
  deletedOrderItems: OrderItem[],
  tx?: Prisma.TransactionClient
) => {
  if (deletedOrderItems.length === 0) return;

  const db = tx ?? prisma;

  // Seats release by DELETING their override — "available" is the absence of a
  // row — and their section entity status must never be touched.
  const seatItems = deletedOrderItems.filter(
    (item) =>
      item.seatSectionId != null && item.row != null && item.seat != null
  );
  if (seatItems.length > 0) {
    await releaseSeats(
      seatItems.map((item) => ({
        seatSectionId: item.seatSectionId!,
        row: item.row!,
        seat: item.seat!,
      })),
      db
    );
  }

  // Extract entity IDs from deleted items, filtering out any undefined values
  const entityIdsToRelease = deletedOrderItems
    .filter((item) => item.seatSectionId == null)
    .map((item) => item.entityId)
    .filter((id): id is string => Boolean(id));

  // Update entities to make them available again and clear associations
  if (entityIdsToRelease.length > 0) {
    await db.entity.updateMany({
      where: { id: { in: entityIdsToRelease } },
      data: {
        status: EntityStatus.AVAILABLE,
        exhibitorId: null,
        invitationAttendeeId: null,
        sponsorId: null,
      },
    });
  }
};

const calculateDeletedItemsTotalAndTax = (deletedOrderItems: OrderItem[]) => {
  let deletedItemsTotal = 0;
  let deletedItemsTax = 0;

  deletedOrderItems.forEach((item) => {
    deletedItemsTotal += item.total ?? 0;
    deletedItemsTax += item.tax ?? 0;
  });

  return { deletedItemsTotal, deletedItemsTax };
};

/**
 * Updates line items by counting remaining order items and replacing with handler results
 * Handles three cases:
 * 1. Items added (increase qty) - handler creates new line item with finalQuantity
 * 2. Items removed (decrease qty) - count remaining order items and update quantity
 * 3. Items fully deleted - remove line item completely
 *
 * @param currentLineItems - Current line items from order
 * @param remainingOrderItems - Order items that remain after deletions
 * @param newItemsResults - Results from newly created items with their correct line items
 * @returns Updated line items array
 */
const updateLineItems = (
  orderType: OrderType,
  currentLineItems: LineItem[],
  remainingOrderItems: OrderItem[],
  newItemsResults: Array<{ lineItems: LineItem[] }>
): LineItem[] => {
  const isEntityOrder = orderType === OrderType.ENTITY;

  const lineItemsMap = new Map<string, LineItem>();

  // Step 1: Count remaining order items by ticketId/entityId
  const remainingQuantityMap = new Map<string, number>();
  remainingOrderItems.forEach((item) => {
    // Seat line items are keyed per seat — a section has one line item per seat
    const itemId =
      isEntityOrder && item.seatSectionId != null
        ? `${item.seatSectionId}:${item.row}:${item.seat}`
        : isEntityOrder
        ? item.entityId
        : item.ticketId;

    if (itemId) {
      remainingQuantityMap.set(
        itemId,
        (remainingQuantityMap.get(itemId) || 0) + 1
      );
    }
  });

  // Step 2: Update existing line items with remaining quantities
  if (currentLineItems && Array.isArray(currentLineItems)) {
    currentLineItems.forEach((lineItem) => {
      const remainingQty = remainingQuantityMap.get(lineItem.id);
      if (remainingQty !== undefined && remainingQty > 0) {
        // Item still exists, update quantity and price
        const unitPrice = lineItem.price / lineItem.quantity;
        lineItemsMap.set(lineItem.id, {
          ...lineItem,
          quantity: remainingQty,
          price: unitPrice * remainingQty,
        });
      }
      // If remainingQty is 0 or undefined, item was fully deleted
    });
  }

  // Step 3: Replace/add line items from handlers (for newly created items)
  // Handlers create line items with correct finalQuantity and price
  newItemsResults.forEach((result) => {
    result.lineItems.forEach((newLineItem) => {
      // Replace existing or add new - handlers have already calculated everything correctly
      lineItemsMap.set(newLineItem.id, newLineItem);
    });
  });

  return Array.from(lineItemsMap.values());
};

const processItemsRemoval = async (
  currentOrderItems: OrderItem[],
  toDelete: string[],
  orderType: OrderType,
  tx: Prisma.TransactionClient
) => {
  const isEntityOrder = orderType === OrderType.ENTITY;

  await deleteItemsFormResults(toDelete, tx);

  if (toDelete.length > 0) {
    await tx.dTCMOrderItem.deleteMany({
      where: { orderItemId: { in: toDelete } },
    });
  }

  // Filter order items that are marked for deletion
  const deletedOrderItems = currentOrderItems.filter((item) =>
    toDelete.includes(item.id)
  );

  if (isEntityOrder) {
    await releaseEntities(deletedOrderItems, tx);
  }

  return calculateDeletedItemsTotalAndTax(deletedOrderItems);
};

/**
 * Checks if an order item has completed form results
 *
 * This function queries the database to determine if there are any completed form results
 * associated with the specified order item. It looks for form results of type
 * ORDER_ITEM or ORDER_ITEM_REQUEST that have a status of COMPLETED.
 *
 * @param orderItemId - The ID of the order item to check for completed forms
 * @param tx - The Prisma transaction client for database operations
 * @returns Promise<boolean> - True if completed form results exist, false otherwise
 */
const hasCompletedFormsResults = async (
  orderItemId: string,
  tx: Prisma.TransactionClient
) => {
  const completedResults = await tx.formResultEntity.findMany({
    where: {
      orderItemId: orderItemId,
      type: {
        in: [FormResultType.ORDER_ITEM, FormResultType.ORDER_ITEM_REQUEST],
      },
      formResult: {
        status: FormResultStatus.COMPLETED,
      },
    },
    select: {
      type: true,
      formResult: {
        select: {
          surveyResult: true,
        },
      },
    },
  });

  const hasCompletedItemRequestForm = completedResults.some(
    (result) =>
      result.type === FormResultType.ORDER_ITEM_REQUEST &&
      result.formResult?.surveyResult
  );
  const hasCompletedItemForm = completedResults.some(
    (result) =>
      result.type === FormResultType.ORDER_ITEM &&
      result.formResult?.surveyResult
  );

  // Merge survey results with ORDER_ITEM type taking priority
  const mergedSurveyResult: Record<string, any> = {};

  // First process ORDER_ITEM_REQUEST results
  completedResults
    .filter((result) => result.type === FormResultType.ORDER_ITEM_REQUEST)
    .forEach((result) => {
      if (result.formResult?.surveyResult) {
        Object.assign(mergedSurveyResult, result.formResult.surveyResult);
      }
    });

  // Then process ORDER_ITEM results (overrides any existing values)
  completedResults
    .filter((result) => result.type === FormResultType.ORDER_ITEM)
    .forEach((result) => {
      if (result.formResult?.surveyResult) {
        Object.assign(mergedSurveyResult, result.formResult.surveyResult);
      }
    });

  return {
    hasCompletedBothForms: hasCompletedItemRequestForm && hasCompletedItemForm,
    surveyResult:
      Object.keys(mergedSurveyResult).length > 0
        ? mergedSurveyResult
        : undefined,
  };
};

/**
 * Process a booth order item, creating exhibitor and validating forms
 */
const processBoothItem = async (
  item: OrderItem,
  boothLabel: string,
  eventId: string,
  languagesIds: string[],
  orderFormResult: Record<string, any> | undefined,
  tx: Prisma.TransactionClient
) => {
  const { surveyResult, hasCompletedBothForms } =
    await hasCompletedFormsResults(item.id, tx);

  const isItemPendingApproval =
    item.status === OrderItemStatus.PENDING_APPROVAL;
  const isItemApproved = item.status === OrderItemStatus.APPROVED;

  // Validate forms for pending approval items
  if ((isItemPendingApproval || isItemApproved) && !hasCompletedBothForms) {
    return {
      exhibitorId: undefined,
      shouldConfirm: false,
    };
  }

  // Merge order form results with item form results
  const formsResultsToCreateExhibitor = {
    ...orderFormResult,
    ...surveyResult,
  };

  // Create booth exhibitor
  const exhibitorId = await createBoothExhibitor(
    eventId,
    boothLabel,
    languagesIds,
    formsResultsToCreateExhibitor,
    tx
  );

  // Return processing results
  return {
    exhibitorId,
    shouldConfirm: true,
  };
};

const createBoothExhibitor = async (
  eventId: string,
  boothLabel: string,
  languagesIds: string[],
  surveyResult: Record<string, any>,
  tx: Prisma.TransactionClient
) => {
  let exhibitorId: string | undefined;

  const exhibitorEmail = surveyResult.email;
  const isExhibitorEmailExist = await tx.exhibitor.findFirst({
    where: {
      email: exhibitorEmail,
      eventId,
    },
    select: { id: true },
  });

  if (isExhibitorEmailExist) {
    exhibitorId = isExhibitorEmailExist.id;
  } else {
    const data: Partial<Exhibitor> = {
      boothName: boothLabel,
      email: exhibitorEmail || "",
      logo: surveyResult.logo || "",
      phone: surveyResult.phone || "",
      location: surveyResult.location || "",
      website: surveyResult.website || "",
      facebook: surveyResult.facebook || "",
      linkedin: surveyResult.linkedin || "",
      x: surveyResult.x || "",
      instagram: surveyResult.instagram || "",
      youtube: surveyResult.youtube || "",
    };
    const translations: Prisma.ExhibitorTranslationsCreateManyExhibitorInput[] =
      languagesIds.map((languageId) => ({
        languageId,
        name: surveyResult.name || "",
        contactName: surveyResult.contactName || "",
        bio: surveyResult.bio || "",
        tags: surveyResult.tags ? [surveyResult.tags] : [],
        adminNotes: surveyResult.adminNotes || "",
        description: surveyResult.description || "",
      }));

    const exhibitor = await tx.exhibitor.create({
      data: {
        ...data,
        eventId,
        translations: {
          createMany: {
            data: translations,
          },
        },
      },
      select: { id: true },
    });

    exhibitorId = exhibitor.id;
  }

  return exhibitorId;
};

/** Same enriched shape as a successful `updateOrderV2` transaction return — used for idempotent duplicate finalize. */
async function snapshotCompletedOrderV2Result(orderId: string, lang?: string) {
  const langCode = lang ?? "en";

  const order = await prisma.order.findUnique({
    where: { id: orderId },
    include: {
      orderItems: true,
      payments: true,
    },
  });

  if (!order) {
    throw new Error("Order not found");
  }

  const orderFormRow = await prisma.order.findUnique({
    where: { id: orderId },
    select: {
      formResults: {
        take: 1,
        select: { formResult: true },
      },
    },
  });
  const orderFormResult = orderFormRow?.formResults?.[0]?.formResult;

  const updatedOrderItems = await prisma.orderItem.findMany({
    where: { orderId },
    include: {
      formResults: {
        select: {
          type: true,
          formResult: {
            select: {
              id: true,
              status: true,
              surveyResult: true,
            },
          },
        },
      },
      ticket: {
        select: {
          id: true,
          bookableItem: {
            select: {
              id: true,
              translations: {
                where: {
                  language: {
                    code: langCode,
                  },
                },
                select: {
                  name: true,
                  description: true,
                },
              },
              formEntities: {
                select: {
                  type: true,
                  formTemplate: {
                    select: {
                      id: true,
                      json: true,
                    },
                  },
                },
              },
            },
          },
        },
      },
      entity: {
        select: {
          id: true,
          label: true,
          type: true,
          properties: {
            select: {
              enableBookingRequest: true,
            },
          },
          floorPlan: {
            select: {
              settings: {
                select: {
                  enableBookingRequest: true,
                },
              },
              formEntities: {
                select: {
                  type: true,
                  formTemplate: {
                    select: {
                      id: true,
                      json: true,
                    },
                  },
                },
              },
            },
          },
        },
      },
      invitationAttendee: {
        select: {
          id: true,
          invitation: {
            select: {
              id: true,
              translations: {
                where: {
                  language: {
                    code: langCode,
                  },
                },
                select: {
                  name: true,
                  description: true,
                },
              },
              formEntity: {
                select: {
                  formTemplate: {
                    select: {
                      id: true,
                      json: true,
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  });

  return {
    ...order,
    formResult: orderFormResult,
    orderItems: updatedOrderItems,
  };
}

interface UpdateOrderV2Type extends Omit<UpdateOrderV2Payload, "isDTCM"> {
  createdBy: {
    id?: string;
    name?: string;
  };
}

/**
 * Update an existing order with optimized performance
 *
 * Flow:
 * 1. Fetch order and validate not expired
 * 2. Calculate diff between current and new selections
 * 3. Process item changes:
 *    - Delete removed items and release entities
 *    - Create new items using handlers (reused from createOrderV2)
 *    - Recalculate totals for changed items
 * 4. Validate payment gateway for paid orders
 * 5. Ensure payment settled before completion for paid orders if payment gateway is not delayed
 * 6. Update order item and entity statuses when completing
 * 7. Create order transition if status changed
 *
 * @param orderId - The order ID to update
 * @param payload - The update payload with selectedItems and/or status
 * @returns The updated order with order items
 */
export const updateOrderV2 = async (
  orderId: string,
  payload: UpdateOrderV2Type
) => {
  try {
    const order = await prisma.order.findUnique({
      where: { id: orderId },
      select: {
        id: true,
        type: true,
        status: true,
        lineItems: true,
        orderItems: true,
        expiryDate: true,
        subTotal: true,
        tax: true,
        total: true,
        taxRate: true,
        payments: true,
        completedAt: true,
        formResults: {
          take: 1,
          select: { formResult: true },
        },
        currency: true,
        eventId: true,
        event: {
          select: {
            id: true,
            currency: true,
            languagesIds: true,
            eventMethodConfigs: {
              where: { disabled: false },
              select: {
                gatewayMethod: {
                  select: {
                    gatewayConfiguration: {
                      select: {
                        paymentGateway: { select: { disabled: true } },
                      },
                    },
                  },
                },
              },
            },
            restrictions: {
              where: {
                enabled: true,
              },
            },
            taxRegion: {
              select: { saleTaxRate: true },
            },
          },
        },
      },
    });
    if (!order) {
      throw new Error("Order not found");
    }

    const {
      type,
      status: currentStatus,
      completedAt: currentCompletedAt,
      orderItems,
      lineItems,
      expiryDate,
      event,
      subTotal,
      tax,
      total,
      taxRate: orderTaxRate,
      payments,
      formResults,
    } = order;

    const {
      selectedItems,
      status: newStatus,
      selectedPaymentMethodId,
      createdBy,
      lang,
      profileId,
      appliedPricingRules,
    } = payload;

    if (
      currentStatus === OrderStatus.COMPLETED &&
      newStatus === OrderStatus.COMPLETED &&
      selectedItems === undefined
    ) {
      return await snapshotCompletedOrderV2Result(orderId, lang);
    }

    // Validate restrictions if order type is TICKET or ENTITY
    // Validation works even without profileId (validates current selection only)
    if (
      (type === OrderType.TICKET || type === OrderType.ENTITY) &&
      event?.restrictions?.length > 0
    ) {
      const restrictionValidation = await validateOrderRestrictions(
        event?.restrictions,
        profileId,
        type === OrderType.TICKET ? "TICKET" : "ENTITY",
        selectedItems || []
      );
      if (!restrictionValidation.valid) {
        throw new Error(restrictionValidation.error);
      }
    }
    const updatedOrder = await prisma.$transaction(
      async (tx) => {
        const orderFormResult = formResults?.[0]?.formResult;

        const isExpired =
          currentStatus === OrderStatus.EXPIRED ||
          (expiryDate &&
            expiryDate < new Date() &&
            currentStatus !== OrderStatus.COMPLETED);
        const isEntityOrder = type === OrderType.ENTITY;

        if (isExpired) {
          throw new Error(`This order is expired`);
        }

        // Check if order is being completed
        const isCompletingOrder = newStatus === OrderStatus.COMPLETED;

        // Order modification guards - prevent modifying orders in terminal states (completed, canceled, expired, deleted and new status is not used when updating order!)
        const hasItemsModification = selectedItems && selectedItems.length > 0;
        const hasRequestRevisionItems = orderItems.some(
          (item) => item.status === OrderItemStatus.REQUEST_REVISION
        );
        if (
          hasItemsModification &&
          currentStatus !== OrderStatus.ON_GOING &&
          //! this is a special case for entity order completion
          //! we might have completed order with items still pending approval
          !(
            isEntityOrder &&
            currentStatus === OrderStatus.COMPLETED &&
            isCompletingOrder
          ) &&
          //! allow modification of completed orders that have items pending revision
          !(currentStatus === OrderStatus.COMPLETED && hasRequestRevisionItems)
        ) {
          throw new Error("Cannot modify items in a non-ongoing order");
        }

        const {
          id: eventId,
          currency,
          taxRegion,
          eventMethodConfigs,
          languagesIds,
        } = event;

        const paymentGateways = eventMethodConfigs
          .map(
            (config) =>
              config.gatewayMethod.gatewayConfiguration?.paymentGateway
          )
          .filter(
            (gateway): gateway is NonNullable<typeof gateway> => !!gateway
          );

        const { isDelayedPaymentGateway } =
          isCompletingOrder && selectedPaymentMethodId
            ? await getIsDelayedPaymentGateway(selectedPaymentMethodId, tx)
            : { isDelayedPaymentGateway: false };

        // Calculate pricing and totals
        const taxRate = taxRegion?.saleTaxRate || orderTaxRate || 0;
        const isEventTaxConfigured = Boolean(taxRegion);

        // TODO: Tax Rate Consistency Issue - Discuss with team
        // Current behavior: New items use event's current tax rate, existing items keep their original tax rate
        // This means if event tax changes from 10% to 15%:
        // - Existing order items: still have 10% tax
        // - Newly added items: will have 15% tax
        // - Final order: mixed tax rates (some items 10%, some 15%)
        //
        // Questions for team:
        // 1. Is this the desired behavior? Should new items reflect current pricing?
        // 2. Or should ALL items (existing + new) use the same tax rate (order's original rate)?
        // 3. Should we recalculate existing items if tax rate changes significantly?
        // 4. How should this be displayed on receipts/invoices?
        //
        // Current implementation: taxRate = taxRegion?.saleTaxRate || orderTaxRate || 0
        // Alternative (all same rate): taxRate = orderTaxRate || 0

        // Calculate diff using optimized function
        const { toDelete, toCreate } = selectedItems
          ? calculateOrderItemsDiff(type, orderItems, selectedItems)
          : { toDelete: [], toCreate: [] };

        const hasItemsToCreate = toCreate.length > 0;
        const hasItemsToDelete = toDelete.length > 0;
        const hasItemsChanged = hasItemsToCreate || hasItemsToDelete;

        let newSubTotal = subTotal || 0;
        let newTax = tax || 0;
        let newTotal = total || 0;

        const newOrderItems: Prisma.OrderItemCreateManyOrderInput[] = [];
        let newLineItems: LineItem[] = lineItems || [];

        // Process item changes if selection was updated
        if (hasItemsChanged && hasItemsModification) {
          if (hasItemsToDelete) {
            // Process items removal
            const { deletedItemsTotal, deletedItemsTax } =
              await processItemsRemoval(orderItems, toDelete, type, tx);

            // Subtract deleted items from totals
            newSubTotal -= deletedItemsTotal;
            newTax -= deletedItemsTax;
          }

          // Get remaining order items (not deleted)
          const remainingOrderItems = orderItems.filter(
            (item) => !toDelete.includes(item.id)
          );

          const newItemsResults: Array<{
            itemsSubTotal: number;
            itemsTax: number;
            orderItems: Prisma.OrderItemCreateManyOrderInput[];
            lineItems: LineItem[];
          }> = [];

          if (hasItemsToCreate) {
            // Process new items to create
            const results = await Promise.all(
              toCreate.map(async (item) => {
                const {
                  entityId,
                  ticketId,
                  inviteeId,
                  quantity: requestedQuantity,
                  finalQuantity,
                  label,
                  row,
                  seat,
                } = item;

                if (isEntityOrder) {
                  if (!entityId) {
                    throw new Error(
                      "Entity Id is required for ENTITY order type"
                    );
                  }

                  const {
                    itemSubTotal,
                    itemTax,
                    orderItem,
                    lineItem,
                    entityTier,
                  } = await createEntityOrderItemHandler(
                    entityId,
                    {
                      requestedQuantity,
                      currency,
                      taxRate,
                      isEventTaxConfigured,
                      inviteeId,
                      label,
                      ...(row != null && seat != null
                        ? {
                            seat: { row, seat },
                            expiryDate: expiryDate ?? undefined,
                          }
                        : {}),
                    },
                    tx
                  );

                  return {
                    itemsSubTotal: itemSubTotal,
                    itemsTax: itemTax,
                    orderItems: [orderItem],
                    lineItems: [lineItem],
                    entityTier,
                  };
                } else {
                  if (!ticketId) {
                    throw new Error(
                      "Ticket Id is required for TICKET order type"
                    );
                  }

                  const {
                    itemsSubTotal,
                    itemsTax,
                    orderItems: ticketOrderItems,
                    lineItems: ticketLineItems,
                  } = await createTicketOrderItemsHandler(
                    ticketId,
                    {
                      requestedQuantity,
                      finalQuantity,
                      currency,
                      taxRate,
                      isEventTaxConfigured,
                      inviteeId,
                      label,
                    },
                    tx
                  );

                  return {
                    itemsSubTotal,
                    itemsTax,
                    orderItems: ticketOrderItems,
                    lineItems: ticketLineItems,
                  };
                }
              })
            );
            checkAmounts(
              results as {
                entityTier: {
                  id: string;
                  quantity: { max: number; min: number; soldTogether: number };
                  name: string;
                };
              }[]
            );
            // Accumulate results for totals
            newItemsResults.push(...results);
            for (const result of results) {
              newSubTotal += result.itemsSubTotal;
              newTax += result.itemsTax;
              newOrderItems.push(...result.orderItems);
            }
          }

          // Update line items: count remaining items and replace with handler results
          newLineItems = updateLineItems(
            type,
            lineItems,
            remainingOrderItems,
            newItemsResults
          );
        }

        // Process pricing rules if completing order with applied rules
        // This must be done BEFORE calculating final totals
        let calculationResult: PricingCalculationResult | null = null;
        let createAudits: (() => Promise<void>) | null = null;

        if (isCompletingOrder && appliedPricingRules) {
          const result = await processPricingRulesForOrder(
            order as unknown as Order & { orderItems: OrderItem[] },
            appliedPricingRules,
            selectedPaymentMethodId,
            taxRate,
            tx
          );

          calculationResult = result.calculationResult;
          createAudits = result.createAudits;

          // Use calculated totals from pricing engine
          newSubTotal = calculationResult.orderResult.finalTotal;

          // Calculate total tax from all sources
          const totalItemTax = calculationResult.itemResults.reduce(
            (sum, item) => sum + item.itemTaxTotal,
            0
          );
          const totalItemChargeTax = calculationResult.itemResults.reduce(
            (sum, item) => sum + item.chargeTaxTotal,
            0
          );
          const orderChargeTax = calculationResult.orderResult.chargeTaxTotal;
          newTax = totalItemTax + totalItemChargeTax + orderChargeTax;

          // Final total is the finalPayable from engine
          newTotal = calculationResult.finalPayable;
        } else {
          // Calculate new total the old way if no pricing rules
          newTotal = newSubTotal + newTax;
        }

        const isPaidOrder = newTotal > 0;
        const hasPaymentGatewayConfigured = paymentGateways.length > 0;
        const hasPaymentGatewayEnabled = paymentGateways.some(
          (gateway) => gateway.disabled === false
        );

        /**
         * Cleanup AWAITING_PAYMENT records when order transitions from paid to free.
         * Prevents virtual status from incorrectly showing AWAITING_PAYMENT for free orders.
         * Deletes AWAITING_PAYMENT records when order transitions from paid to free.
         * If order remains paid, all payment records are preserved.
         */
        const oldTotal = total || 0;
        const isTransitionedFromPaidToFree = newTotal === 0 && oldTotal > 0;
        if (isTransitionedFromPaidToFree) {
          await tx.payment.deleteMany({
            where: {
              orderId: orderId,
              status: PaymentStatus.AWAITING_PAYMENT,
            },
          });
        }

        // Validate payment gateway for paid orders
        if (isCompletingOrder && isPaidOrder) {
          if (!hasPaymentGatewayConfigured) {
            throw new Error("Please configure payment gateway for paid orders");
          }
          if (!hasPaymentGatewayEnabled) {
            //TODO we should not forget about, discounts/add ticket/edit ticket type, delete ticket/ taxable/ fees e.g price + fees + tax charges - discount + gateway fee + order fees + vat  EXAMPLE etc..
            // TODO: Validate payment window for paid orders later.
            throw new Error("Please enable payment gateway for paid orders");
          }
        }

        // Ensure payment fully settled before completion for paid orders
        if (isCompletingOrder && isPaidOrder && !isDelayedPaymentGateway) {
          const isPaymentFullySettled = ensurePaymentFullySettled(
            newTotal,
            payments
          );

          if (!isPaymentFullySettled) {
            throw new Error("Payment is not fully settled");
          }
        }

        const isCompletedWithoutCompletedAt =
          isCompletingOrder && !currentCompletedAt;

        // Update the order
        const updatedOrder = await tx.order.update({
          where: { id: orderId },
          data: {
            ...(profileId && { profile: { connect: { id: profileId } } }),
            ...(newStatus && { status: newStatus }),
            ...(isCompletedWithoutCompletedAt && { completedAt: new Date() }),
            ...(hasItemsChanged && {
              orderItems: {
                ...(hasItemsToCreate && {
                  createMany: { data: newOrderItems },
                }),
                // Delete items, also means release ticket quantity.
                ...(hasItemsToDelete && {
                  deleteMany: { id: { in: toDelete } },
                }),
              },
              subTotal: newSubTotal,
              tax: newTax,
              total: newTotal,
              lineItems: newLineItems,
            }),
          },
          include: {
            orderItems: true,
            payments: true,
          },
        });

        if (hasItemsToCreate) {
          const dtcmOrder = await tx.dTCMOrder.findUnique({
            where: { orderId },
            select: {
              id: true,
              dtcmOrderItems: { select: { orderItemId: true } },
            },
          });

          if (dtcmOrder) {
            const linkedOrderItemIds = new Set(
              dtcmOrder.dtcmOrderItems.map((item) => item.orderItemId)
            );
            const missingOrderItems = updatedOrder.orderItems.filter(
              (item) => !linkedOrderItemIds.has(item.id)
            );

            if (missingOrderItems.length > 0) {
              await tx.dTCMOrderItem.createMany({
                data: missingOrderItems.map((item) => ({
                  dtcmOrderId: dtcmOrder.id,
                  orderItemId: item.id,
                })),
              });
            }
          }
        }

        // Newly added seats were claimed before their order item existed
        if (hasItemsToCreate) {
          await linkSeatOverridesToOrderItems(updatedOrder.orderItems, tx);
        }

        // Transition REQUEST_REVISION items back to PENDING_APPROVAL
        // when a user submits their revised order form
        if (
          hasRequestRevisionItems &&
          currentStatus === OrderStatus.COMPLETED
        ) {
          const revisionItemIds = orderItems
            .filter((item) => item.status === OrderItemStatus.REQUEST_REVISION)
            .map((item) => item.id);

          if (revisionItemIds.length > 0) {
            await tx.orderItem.updateMany({
              where: { id: { in: revisionItemIds } },
              data: { status: OrderItemStatus.PENDING_APPROVAL },
            });
          }
        }

        // Update order items with calculated values from pricing engine
        if (calculationResult && isCompletingOrder) {
          // Update each order item with its calculated totals in parallel
          // Match by ticketId or entityId
          const itemUpdatePromises = calculationResult.itemResults.map(
            (itemResult) => {
              const orderItemToUpdate = updatedOrder.orderItems.find(
                (oi) =>
                  oi.ticketId === itemResult.itemId ||
                  oi.entityId === itemResult.itemId
              );

              if (!orderItemToUpdate) return null;

              return tx.orderItem.update({
                where: { id: orderItemToUpdate.id },
                data: {
                  tax: itemResult.itemTaxTotal + itemResult.chargeTaxTotal,
                  total:
                    itemResult.finalTotal +
                    itemResult.itemTaxTotal +
                    itemResult.chargeTaxTotal,
                },
              });
            }
          );

          // Execute all updates in parallel
          await Promise.all(itemUpdatePromises.filter(Boolean));
        }

        if (isCompletingOrder && (!isDelayedPaymentGateway || !isPaidOrder)) {
          /**
           * SMART ORDER ITEM AND ENTITY STATUS UPDATE LOGIC
           *
           * Handles order item status updates during order completion:
           * - PENDING_APPROVAL items: Only confirm if booking forms are completed form entity order type
           * - RESERVED items: Confirm immediately (no approval required)
           * - Free orders (total = 0): Always confirm items, even if a stale payment method was sent
           * - Terminal states: CANCELLED, EXPIRED, etc. are preserved
           * - Entity reservation: Only reserve entities for confirmed items
           */

          // Get all final order items after updates
          const finalOrderItems = updatedOrder.orderItems;
          const entityIdsToLoad = Array.from(
            new Set(
              finalOrderItems
                .map((item) => item.entityId)
                .filter((entityId): entityId is string => Boolean(entityId))
            )
          );
          const entitiesById = new Map(
            (entityIdsToLoad.length > 0
              ? await tx.entity.findMany({
                  where: { id: { in: entityIdsToLoad } },
                  select: { id: true, type: true, label: true },
                })
              : []
            ).map((entity) => [entity.id, entity] as const)
          );

          const itemsIdsToConfirm: string[] = [];
          const entitiesIdsToReserve: string[] = [];
          let exhibitorId: string | undefined;

          // Process each order item based on its current status
          for (const item of finalOrderItems) {
            const isItemReserved = item.status === OrderItemStatus.RESERVED;
            const isItemPendingApproval =
              item.status === OrderItemStatus.PENDING_APPROVAL;
            const isItemApproved = item.status === OrderItemStatus.APPROVED;

            // Skip items that don't need processing
            if (!isItemReserved && !isItemPendingApproval && !isItemApproved) {
              continue;
            }

            // Handle non-entity orders or items without entityId
            if (!isEntityOrder || !item.entityId) {
              if (isItemReserved) {
                itemsIdsToConfirm.push(item.id);
              }
              continue;
            }

            // Get entity to check its type
            // TODO: later we can send entity type with selectedItems in the payload
            const entity = entitiesById.get(item.entityId);

            if (!entity) {
              throw new Error(`Entity with id ${item.entityId} not found`);
            }

            const { label, type } = entity;
            // Handle BOOTH type entities
            if (type === EntityType.BOOTH) {
              const orderFormSurveyResult =
                orderFormResult?.surveyResult as Record<string, any>;

              const result = await processBoothItem(
                item,
                label,
                eventId,
                languagesIds,
                orderFormSurveyResult,
                tx
              );

              exhibitorId = result.exhibitorId;

              if (result.shouldConfirm) {
                itemsIdsToConfirm.push(item.id);
                entitiesIdsToReserve.push(item.entityId);
              }

              // BOOTH orders can only contain one BOOTH entity (one order item)
              break;
            }

            // Handle non-booth entities like seats (only reserved items need confirmation)
            if (isItemReserved) {
              itemsIdsToConfirm.push(item.id);
              // A section stays AVAILABLE — the sold seat is the override row
              if (type !== EntityType.SEATS_SECTION) {
                entitiesIdsToReserve.push(item.entityId);
              }
            }
          }

          if (itemsIdsToConfirm.length > 0) {
            await tx.orderItem.updateMany({
              where: { id: { in: itemsIdsToConfirm } },
              data: {
                status: OrderItemStatus.CONFIRMED,
                ...(exhibitorId && { exhibitorId }),
                // ...(invitationAttendeeId && { invitationAttendeeId }),
              },
            });

            // Update the status in memory to reflect the database changes
            updatedOrder.orderItems = updatedOrder.orderItems.map((i) => {
              if (itemsIdsToConfirm.includes(i.id)) {
                return {
                  ...i,
                  status: OrderItemStatus.CONFIRMED,
                  ...(exhibitorId && { exhibitorId }),
                };
              }
              return i;
            });
          }

          if (entitiesIdsToReserve.length > 0) {
            await tx.entity.updateMany({
              where: { id: { in: entitiesIdsToReserve } },
              data: {
                status: EntityStatus.RESERVED,
                ...(exhibitorId && { exhibitorId }),
              },
            });
          }

          // Sold seats: keep the override, drop the expiry so no sweep reclaims it
          await confirmSeatsByOrderItemIds(itemsIdsToConfirm, tx);
        }

        // Create audit records for pricing rules (calculation already done earlier)
        if (createAudits && isCompletingOrder) {
          await createAudits();
        }

        // Create order transition if status changed
        if (newStatus && newStatus !== currentStatus) {
          const createdByName = createdBy.name || CREATED_BY.GUEST;
          const createdById = createdBy.id || CREATED_BY.GUEST;

          await createOrderTransitionV2(
            {
              status: newStatus,
              orderId: updatedOrder.id,
              createdBy: createdByName,
              metaData: {
                previousStatus: currentStatus,
                createdBy: createdByName,
                createdById,
              },
            },
            tx
          );
        }

        // TODO: Create order item transitions for updated items

        const updatedOrderItems = await tx.orderItem.findMany({
          where: { orderId: updatedOrder.id },
          include: {
            formResults: {
              select: {
                type: true,
                formResult: {
                  select: {
                    id: true,
                    status: true,
                    surveyResult: true,
                  },
                },
              },
            },
            ticket: {
              select: {
                id: true,
                bookableItem: {
                  select: {
                    id: true,
                    translations: {
                      where: {
                        language: {
                          code: lang,
                        },
                      },
                      select: {
                        name: true,
                        description: true,
                      },
                    },
                    formEntities: {
                      select: {
                        type: true,
                        formTemplate: {
                          select: {
                            id: true,
                            json: true,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
            entity: {
              select: {
                id: true,
                label: true,
                type: true,
                properties: {
                  select: {
                    enableBookingRequest: true,
                  },
                },
                floorPlan: {
                  select: {
                    settings: {
                      select: {
                        enableBookingRequest: true,
                      },
                    },
                    formEntities: {
                      select: {
                        type: true,
                        formTemplate: {
                          select: {
                            id: true,
                            json: true,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
            invitationAttendee: {
              select: {
                id: true,
                state: true,
                contact: {
                  select: {
                    id: true,
                    name: true,
                    email: true,
                    phoneNumber: true,
                  },
                },
                invitation: {
                  select: {
                    id: true,
                    translations: {
                      where: {
                        language: {
                          code: lang,
                        },
                      },
                      select: {
                        name: true,
                        description: true,
                      },
                    },
                    formEntity: {
                      select: {
                        formTemplate: {
                          select: {
                            id: true,
                            json: true,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });

        return {
          ...updatedOrder,
          formResult: orderFormResult,
          orderItems: updatedOrderItems,
        };
      },
      {
        timeout: 10000,
        maxWait: 10000,
      }
    );

    if (updatedOrder.status === OrderStatus.COMPLETED) {
      warmCheckinRosterEntriesForOrder(updatedOrder.id).catch(() => undefined);

      const hasInvitationAttendees = updatedOrder.orderItems?.some(
        (item) => item.invitationAttendeeId
      );

      if (hasInvitationAttendees) {
        await syncInvitationAttendeesAfterOrderComplete(updatedOrder.id);

        const refreshedAttendees = await prisma.invitationAttendee.findMany({
          where: {
            id: {
              in: updatedOrder.orderItems
                .map((item) => item.invitationAttendeeId)
                .filter((id): id is string => Boolean(id)),
            },
          },
          select: {
            id: true,
            state: true,
            contact: {
              select: {
                id: true,
                name: true,
                email: true,
                phoneNumber: true,
              },
            },
            invitation: {
              select: {
                id: true,
                translations: {
                  where: {
                    language: {
                      code: lang,
                    },
                  },
                  select: {
                    name: true,
                    description: true,
                  },
                },
                formEntity: {
                  select: {
                    formTemplate: {
                      select: {
                        id: true,
                        json: true,
                      },
                    },
                  },
                },
              },
            },
          },
        });

        const attendeesById = new Map(
          refreshedAttendees.map((attendee) => [attendee.id, attendee])
        );

        updatedOrder.orderItems = updatedOrder.orderItems.map((item) => {
          if (!item.invitationAttendeeId) {
            return item;
          }

          const refreshedAttendee = attendeesById.get(
            item.invitationAttendeeId
          );
          if (!refreshedAttendee) {
            return item;
          }

          return {
            ...item,
            invitationAttendee: refreshedAttendee,
          };
        });
      }
    }

    return updatedOrder;
  } catch (error) {
    throw new Error(`Failed to update order: ${error}`);
  }
};
